import { c as _c } from "react/compiler-runtime";
import { Stack, Typography } from '@mui/material';
import { type Condition, ConditionOperatorEnum } from '@repo/api-gw-sdk';
import { useMemo, useState } from 'react';
import { AddButton } from '@/components/buttons/addButton';
import { defaultOperatorFor } from '@/components/conditions/OperatorSelect';
import { createEmptyCondition } from '@/components/conditions/conditionFactory';
import { FilterOperatorSelect } from '@/components/conditions/filterOperatorSelect';
import { NotDiscoveredHint } from '@/components/conditions/notDiscoveredHint';
import { optionListStyles } from '@/components/conditions/optionListGeometry';
import { SelectionPanel } from '@/components/panels/selectionPanel/selectionPanel';
import { ScrollableContent } from '@/components/scroll/scrollableContent';
import { ScrollableWrapper } from '@/components/scroll/scrollableContext';
import { ScrollableHeader } from '@/components/scroll/scrollableHeader';
import { useVirtualizedScrollShadow } from '@/components/scroll/useScrollable';
import { TextDetailsTuple } from '@/components/text/textDetailsTuple';
import { createCombineCondition, findValueCondition, type FilterOption } from '@/types/advanceFilter';
import { pluralize } from '@/utils/string';
import { tagEntityLabel } from './tagKeyValueUtils';
import { TagSearchBoxes } from './tagSearchBoxes';
import type { TagsDataResult, TagsConditionProps } from './tagsData';
import { usePipelineTagsData, useResourceTagsData, useSourceAccountTagsData } from './tagsData';
import { useTagOptions } from './useTagOptions';
import { MultiSelectFilter } from '../filters';
import { CLOUD_PROVIDER_PROPERTY } from '../propertyNames';

/**
 * Keeps the operator the condition already carries; only a brand-new condition gets
 * the default. An emptied selection keeps its condition, valueless, the way the
 * multi-select panel leaves one — the hosts drop a condition with nothing in it.
 */
const withSelectedTags = (props: TagsConditionProps, values: string[]): Condition[] => {
  const existingCondition = findValueCondition(props.conditions, props.property.name);
  return props.conditions.filter(x => x !== existingCondition).concat({
    property: props.property.name,
    operator: existingCondition?.operator ?? defaultOperatorFor(props.property),
    value: values
  });
};
function useProviderFilter(props) {
  const $ = _c(2);
  let t0;
  bb0: {
    if (!props.supportedProviders?.length) {
      t0 = undefined;
      break bb0;
    }
    let t1;
    if ($[0] !== props.supportedProviders) {
      t1 = createCombineCondition([{
        property: CLOUD_PROVIDER_PROPERTY,
        operator: ConditionOperatorEnum.In,
        value: props.supportedProviders
      }]);
      $[0] = props.supportedProviders;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    t0 = t1;
  }
  return t0;
}
const ResourceTagsConditionWrapper = props => {
  const $ = _c(3);
  const filter = useProviderFilter(props);
  const tagsData = useResourceTagsData(filter);
  let t0;
  if ($[0] !== props || $[1] !== tagsData) {
    t0 = <TagsConditionContent {...props} tagsData={tagsData} />;
    $[0] = props;
    $[1] = tagsData;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};
const SourceAccountTagsConditionWrapper = props => {
  const $ = _c(3);
  const filter = useProviderFilter(props);
  const tagsData = useSourceAccountTagsData(filter);
  let t0;
  if ($[0] !== props || $[1] !== tagsData) {
    t0 = <TagsConditionContent {...props} tagsData={tagsData} />;
    $[0] = props;
    $[1] = tagsData;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};
const PipelineTagsConditionWrapper = props => {
  const $ = _c(3);
  const tagsData = usePipelineTagsData();
  let t0;
  if ($[0] !== props || $[1] !== tagsData) {
    t0 = <TagsConditionContent {...props} tagsData={tagsData} />;
    $[0] = props;
    $[1] = tagsData;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};
export const TagsCondition = props => {
  const $ = _c(6);
  if (props.dataSource === "pipelineTags") {
    let t0;
    if ($[0] !== props) {
      t0 = <PipelineTagsConditionWrapper {...props} />;
      $[0] = props;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  if (props.dataSource === "sourceAccountTags") {
    let t0;
    if ($[2] !== props) {
      t0 = <SourceAccountTagsConditionWrapper {...props} />;
      $[2] = props;
      $[3] = t0;
    } else {
      t0 = $[3];
    }
    return t0;
  }
  let t0;
  if ($[4] !== props) {
    t0 = <ResourceTagsConditionWrapper {...props} />;
    $[4] = props;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  return t0;
};
const TagsConditionContent = props => {
  const $ = _c(37);
  const [keyFilter, setKeyFilter] = useState("");
  const [valueFilter, setValueFilter] = useState("");
  const {
    data: body,
    isLoading
  } = props.tagsData;
  let t0;
  if ($[0] !== props.conditions || $[1] !== props.property.name) {
    t0 = findValueCondition(props.conditions, props.property.name);
    $[0] = props.conditions;
    $[1] = props.property.name;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const condition = t0;
  let t1;
  if ($[3] !== condition?.value) {
    t1 = condition?.value ?? [];
    $[3] = condition?.value;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const selectedResources = t1;
  const t2 = body?.tags;
  let t3;
  if ($[5] !== keyFilter || $[6] !== props.initialFilterMode || $[7] !== selectedResources || $[8] !== t2 || $[9] !== valueFilter) {
    t3 = {
      tags: t2,
      selectedValues: selectedResources,
      keyFilter,
      valueFilter,
      mode: props.initialFilterMode
    };
    $[5] = keyFilter;
    $[6] = props.initialFilterMode;
    $[7] = selectedResources;
    $[8] = t2;
    $[9] = valueFilter;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const {
    options,
    undiscoveredIds
  } = useTagOptions(t3);
  let t4;
  if ($[11] !== props.initialFilterMode) {
    t4 = tagEntityLabel(props.initialFilterMode);
    $[11] = props.initialFilterMode;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const entityLabel = t4;
  let t5;
  if ($[13] !== props || $[14] !== selectedResources) {
    t5 = item => {
      props.onChange(withSelectedTags(props, selectedResources.filter(x => x !== item)));
    };
    $[13] = props;
    $[14] = selectedResources;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== undiscoveredIds) {
    t6 = item_0 => <Stack sx={{
      gap: "4px",
      overflow: "hidden"
    }}><TextDetailsTuple primaryProp={item_0} />{undiscoveredIds.has(item_0) && <NotDiscoveredHint />}</Stack>;
    $[16] = undiscoveredIds;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== props) {
    t7 = () => props.onChange(withSelectedTags(props, []));
    $[18] = props;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== entityLabel || $[21] !== selectedResources || $[22] !== t5 || $[23] !== t6 || $[24] !== t7) {
    t8 = {
      items: selectedResources,
      entityType: entityLabel,
      onRemove: t5,
      renderItem: t6,
      itemKey: _temp,
      clearAll: t7
    };
    $[20] = entityLabel;
    $[21] = selectedResources;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== entityLabel || $[27] !== keyFilter || $[28] !== options || $[29] !== props || $[30] !== selectedResources || $[31] !== valueFilter) {
    t9 = <ScrollableWrapper><TagsConditionBody {...props} options={options} entityLabel={entityLabel} selectedResources={selectedResources} keyFilter={keyFilter} valueFilter={valueFilter} setKeyFilter={setKeyFilter} setValueFilter={setValueFilter} /></ScrollableWrapper>;
    $[26] = entityLabel;
    $[27] = keyFilter;
    $[28] = options;
    $[29] = props;
    $[30] = selectedResources;
    $[31] = valueFilter;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  let t10;
  if ($[33] !== isLoading || $[34] !== t8 || $[35] !== t9) {
    t10 = <SelectionPanel isLoading={isLoading} drawerProps={t8}>{t9}</SelectionPanel>;
    $[33] = isLoading;
    $[34] = t8;
    $[35] = t9;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  return t10;
};

/** Bridges the chip's operator picker to the `Condition[]` shape the tags panel works in. */
const TagsOperatorSelect = props => {
  const $ = _c(14);
  let t0;
  if ($[0] !== props.conditions || $[1] !== props.property.name) {
    t0 = findValueCondition(props.conditions, props.property.name);
    $[0] = props.conditions;
    $[1] = props.property.name;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const condition = t0;
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      marginY: "12px"
    };
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== condition || $[5] !== props.property) {
    t2 = condition ?? createEmptyCondition(props.property);
    $[4] = condition;
    $[5] = props.property;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== condition || $[8] !== props) {
    t3 = next => props.onChange([...props.conditions.filter(x => x !== condition), next]);
    $[7] = condition;
    $[8] = props;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== props.property || $[11] !== t2 || $[12] !== t3) {
    t4 = <Stack sx={t1}><FilterOperatorSelect condition={t2} property={props.property} onChange={t3} /></Stack>;
    $[10] = props.property;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
};
const TagsConditionBody = props => {
  const $ = _c(27);
  const onScroll = useVirtualizedScrollShadow();
  const filteredValue = props.initialFilterMode === "key" ? props.keyFilter : `${props.keyFilter}=${props.valueFilter}`;
  let t0;
  if ($[0] !== props) {
    t0 = props.panelHost ? <TagsOperatorSelect {...props} /> : <Typography className="my-[12px]" variant="body1">{`Select one or more ${pluralize(props.entityLabel)}`}</Typography>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== filteredValue || $[3] !== props) {
    t1 = !props.panelHost && <AddButton disabled={props.selectedResources.includes(filteredValue) || !props.keyFilter} onClick={() => {
      props.onChange(withSelectedTags(props, [...props.selectedResources, filteredValue]));
    }} />;
    $[2] = filteredValue;
    $[3] = props;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props.initialFilterMode || $[6] !== props.keyFilter || $[7] !== props.setKeyFilter || $[8] !== props.setValueFilter || $[9] !== props.valueFilter || $[10] !== t1) {
    t2 = <TagSearchBoxes mode={props.initialFilterMode} keyFilter={props.keyFilter} valueFilter={props.valueFilter} onKeyFilterChange={props.setKeyFilter} onValueFilterChange={props.setValueFilter} action={t1} />;
    $[5] = props.initialFilterMode;
    $[6] = props.keyFilter;
    $[7] = props.setKeyFilter;
    $[8] = props.setValueFilter;
    $[9] = props.valueFilter;
    $[10] = t1;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== t0 || $[13] !== t2) {
    t3 = <ScrollableHeader hideBorderWhenOnTop={true} className="px-[24px] pt-[12px] pb-[24px]">{t0}{t2}</ScrollableHeader>;
    $[12] = t0;
    $[13] = t2;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== props.options.length || $[16] !== props.panelHost) {
    t4 = props.options.length === 0 && <Typography variant="body1" className="my-[12px]">{props.panelHost ? "No matching values found. Try a new search term." : "No matching values found. Try a new search term, or add this value manually to include it once it\u2019s discovered."}</Typography>;
    $[15] = props.options.length;
    $[16] = props.panelHost;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== onScroll || $[19] !== props) {
    t5 = props.options.length > 0 && <MultiSelectFilter {...props} data-testid={`filter-${props.property.name}`} showSelectAll={!props.panelHost && (!!props.keyFilter || !!props.valueFilter)} onVirtuosoScroll={onScroll} virtuosoStyles={optionListStyles({
      hasSelection: !!props.selectedResources.length,
      searchRows: props.initialFilterMode === "key-value" ? 2 : 1
    })} />;
    $[18] = onScroll;
    $[19] = props;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== t4 || $[22] !== t5) {
    t6 = <ScrollableContent className="px-[24px] pb-[12px]">{t4}{t5}</ScrollableContent>;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== t3 || $[25] !== t6) {
    t7 = <>{t3}{t6}</>;
    $[24] = t3;
    $[25] = t6;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  return t7;
};
function _temp(item_1) {
  return item_1;
}